import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, GraduationCap, Users, Calendar,
  Award, BookOpen, ArrowRight, Shield, Zap, Star,
  Globe, Briefcase, Code, Terminal, MessageSquare
} from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { getBootcampStatus } from "../lib/bootcamp";
import GuessLaunchPrice from "./GuessLaunchPrice";

const BootcampSection = () => {
  const [bootcampStatus, setBootcampStatus] = useState(null);

  useEffect(() => {
    getBootcampStatus().then(data => setBootcampStatus(data));
  }, []);

  return (
    <section className="theme-light section-base bg-background relative py-20 md:py-32 overflow-hidden border-t border-border">
      {/* Background orbs */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-accent/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="border-primary-subtle bg-primary-subtle inline-flex items-center gap-2 px-3 py-1.5 border mb-8">
            <GraduationCap className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Premium Bootcamp</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-8 leading-[1.05] tracking-tight">
            From Python Zero to <span className="text-primary italic">AI Hero</span>
          </h2>
          <p className="font-sora text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
            The most practical path to mastering Machine Learning. Build 12 production-grade projects and get career-ready.
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="bootcamp-grid">
          
          {/* 1. Large Pricing Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-card border-primary/20 pricing-card pos-pricing order-1 border p-10 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4">
               <div className="bg-primary text-background px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm">Best value</div>
            </div>

            <div className="mb-10">
              <span className="text-[11px] text-primary font-semibold block mb-2">Lifetime Access</span>
              <h3 className="text-xl font-bold text-foreground">AI/ML Masterclass</h3>
            </div>
            
            {(!bootcampStatus?.status || (bootcampStatus.status !== 'enrolling' && bootcampStatus.status !== 'live')) ? (
              <div className="mb-12 mt-4">
                <GuessLaunchPrice variant="small" />
              </div>
            ) : (
              <div className="mb-12">
                <div className="flex items-end gap-3 mb-2">
                  <span className="text-5xl font-bold text-foreground">₹4,999</span>
                  <span className="text-muted-foreground text-sm line-through mb-2">₹9,999</span>
                </div>
                <span className="text-accent text-[11px] font-semibold">Limited time offer · 50% Off</span>
              </div>
            )}

            <div className="space-y-6 mb-12 flex-1">
              {[
                "12 Industry Projects",
                "Live Weekly Mentorship",
                "Placement Support",
                "Verified Certificate"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 text-foreground text-sm font-medium">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                  {item}
                </div>
              ))}
            </div>

            {bootcampStatus?.status === 'enrolling' && bootcampStatus.enrollmentDate && (
              <div className="mb-8 p-4 border border-[#F59E0B22] bg-[#F59E0B05] rounded-lg">
                <span className="text-primary text-xs font-semibold block mb-1">Next batch starts</span>
                <span className="text-xl font-bold text-foreground">
                  {format(new Date(bootcampStatus.enrollmentDate), 'MMM dd, yyyy')}
                </span>
              </div>
            )}

            <Link to="/bootcamp" className="mt-auto">
              <Button variant="default" size="lg" className="w-full h-20 text-xl">
                {bootcampStatus?.status === 'enrolling' ? 'ENROLL NOW →' : 'LEARN MORE →'}
              </Button>
            </Link>
            <p className="text-center text-xs text-muted-foreground mt-6">Secure checkout · 30-Day refund</p>
          </motion.div>

          {/* 2. Mentorship Card */}
          <Card className="grid-card pos-mentorship order-2 p-10 flex flex-col gap-8">
            <div className="bg-amber-subtle border-amber-subtle w-14 h-14 border rounded-lg flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-accent" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-foreground mb-3">Live Mentorship</h4>
              <p className="font-sora text-muted-foreground text-sm leading-relaxed">
                Direct access to senior developers. Get code reviews and real-world insights in our interactive weekly sessions.
              </p>
            </div>
            <div className="mt-auto flex -space-x-4 items-center">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-12 h-12 rounded-none border-2 border-card bg-muted" />
              ))}
              <div className="pl-4 text-xs text-accent font-semibold">Active community</div>
            </div>
          </Card>

          {/* 3. Small Cards */}
          {[
            { icon: Briefcase, t: "Real Projects", d: "Build production ML apps", color: "#00E676" },
            { icon: Award, t: "Certification", d: "Industry credentials", color: "#F59E0B" }
          ].map((item, i) => (
            <Card 
              key={i} 
              className={`grid-card order-3 p-8 flex items-center gap-6 hover:bg-white/5 transition-colors ${i === 0 ? 'pos-projects' : 'pos-cert'}`}
            >
              <div style={{ backgroundColor: `${item.color}11`, borderColor: `${item.color}33` }} className="w-12 h-12 border rounded-lg flex items-center justify-center shrink-0">
                <item.icon style={{ color: item.color }} className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground truncate">{item.t}</h4>
                <p className="text-muted-foreground text-xs truncate">{item.d}</p>
              </div>
            </Card>
          ))}

          {/* 6. Curriculum Card */}
          <Card className="grid-card pos-curriculum order-4 p-10 flex flex-col gap-8">
            <div className="bg-primary-subtle border-primary-subtle w-14 h-14 border rounded-lg flex items-center justify-center">
              <Code className="w-8 h-8 text-primary" />
            </div>
            <h4 className="text-base font-semibold text-foreground">Curriculum</h4>
            <div className="space-y-5">
              {[
                { w: "1-3", t: "Python Core" },
                { w: "4-6", t: "Data Science" },
                { w: "7-9", t: "Machine Learning" },
                { w: "10-12", t: "Deep Learning" }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm font-medium py-2 border-b border-gray-100">
                  <span className="text-muted-foreground">Week {item.w}</span>
                  <span className="text-foreground">{item.t}</span>
                </div>
              ))}
            </div>
          </Card>

        </div>
      </div>
    </section>
  );
};

export default BootcampSection;
